import { useSyncExternalStore } from "react";
import type { ExpensePolicy } from "@/api/types";
import { useApiQuery } from "@/api/useApi";

/**
 * The app's (i) tips: their own wording here, which Settings can change. Paragraphs are separated
 * by a blank line.
 */
export const HelpTexts = [
  {
    key: "addIntro",
    label: "How adding works (the (i) by “Multiple Ways to Add”)",
    text: [
      "You can forward your receipts, take a photo of them, drag them here, paste them, or upload them, and AI will automatically read, process, categorize, and add them to the Expense Report for you.",
      "For mileage, you can either enter the start and end locations or the total miles to calculate the reimbursement amount.",
      "Double-check what the AI filled in before you send the report.",
    ].join("\n\n"),
  },
  {
    key: "forwarding",
    label: "Forwarding receipts (the (i) by the email address)",
    text: [
      "Forward any email with a receipt in it, or attach photos or PDFs, from your PlanetArt email.",
      "The AI reads it, fills in the expense and adds it to your draft report, or to Emailed receipts if you don't have one. You'll get a reply saying where it went.",
    ].join("\n\n"),
  },
  {
    key: "tipsSwitch",
    label: "Tips switch (at the top, next to your initials)",
    text: "Tap to show or hide the (i) tips that explain each part of the app. Your choice is remembered on this device.",
  },
  {
    key: "newReport",
    label: "New expense reports (the (i) by “New expense report”)",
    text: [
      "Start a report for each trip or event, like “NYC client visit”, with the dates it covered. Then add its receipts, expenses and mileage.",
      "When everything's on it, send it to your manager to approve. Payroll pays it once it's approved.",
    ].join("\n\n"),
  },
  {
    key: "dragging",
    label: "Dragging receipts (the (i) by “Drag the receipt…”)",
    text: [
      "Drag a receipt from your computer and drop it anywhere on this page: an email straight from Outlook, a photo, or a PDF.",
      "You can drop several at once. The AI reads each one and adds it as an expense for you to check.",
    ].join("\n\n"),
  },
  {
    key: "entryEdit",
    label: "Edit (on an open expense)",
    text: "Change the merchant, amount, date, type or notes, or attach a different receipt.",
  },
  {
    key: "entryMove",
    label: "Move (on an open expense)",
    text: "Move this expense to another of your draft reports.",
  },
  {
    key: "entryRemove",
    label: "Remove (on an open expense)",
    text: "Take this expense off the report. You'll be asked to confirm first.",
  },
  {
    key: "tipPhoto",
    label: "Snap a Photo of It (on a computer)",
    text: "Take a picture of a paper receipt with your camera.",
  },
  {
    key: "tipPhotoQr",
    label: "Snap a Photo of It: under the QR code (on a computer)",
    text: "Scan this with your phone to snap the receipt there. It's added to this report.",
    blankable: true,
  },
  {
    key: "tipPhotoPhone",
    label: "Snap a Photo of It (on a phone)",
    text: "Take a picture of a paper receipt with your camera. You can snap your meal receipt right at the restaurant and it's added straight away.",
  },
  {
    key: "tipPaste",
    label: "Paste It",
    text: "Paste a receipt you copied, like a screenshot or an image.",
  },
  {
    key: "tipUpload",
    label: "Upload It",
    text: "Choose a photo, PDF or saved email from your device.",
  },
  {
    key: "tipManual",
    label: "Manually Enter It",
    text: "Type in an expense yourself, like one without a receipt.",
  },
  {
    key: "tipMileage",
    label: "Enter Mileage",
    text: "Enter where you drove from and to; the miles and amount are worked out for you.",
  },
] as const;

export type HelpKey = (typeof HelpTexts)[number]["key"];

export function defaultHelp(key: HelpKey): string {
  return HelpTexts.find((help) => help.key === key)?.text ?? "";
}

/** Whether a tip shows nothing when cleared in Settings, rather than the app's own wording. */
export function blankable(key: HelpKey): boolean {
  return HelpTexts.some((help) => help.key === key && "blankable" in help);
}

/** A tip's wording: Settings' version when there is one, otherwise the app's own. */
export function helpText(policy: ExpensePolicy | undefined, key: HelpKey): string {
  const own = policy?.helpText?.[key]?.trim();
  return own === "" && blankable(key) ? "" : own || defaultHelp(key);
}

export function useHelp(): (key: HelpKey) => string {
  const { data } = useApiQuery<ExpensePolicy>("/api/expenses/policy");
  return (key) => helpText(data, key);
}

export function paragraphs(text: string): string[] {
  return text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

/* The header's instructions switch: shows or hides the (i)s and tooltips, remembered on this device. */

const showKey = "expenses.show-tips";
const listeners = new Set<() => void>();

function readShow(): boolean {
  try {
    return window.localStorage.getItem(showKey) !== "off";
  } catch {
    return true;
  }
}

export function setShowTips(show: boolean): void {
  try {
    window.localStorage.setItem(showKey, show ? "on" : "off");
  } catch {
    // ignore
  }
  for (const listener of listeners) listener();
}

export function useShowTips(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    readShow,
    () => true,
  );
}
